import { useUser } from '@gat-ship/lib'
import type {
  SupportSession,
  GetSupporSessionsHistoryQueryParams
} from '@gat-ship/types'
import { defineStore } from 'pinia'
import { computed, ref, shallowRef } from 'vue'
import {
  getSupporSessionsHistoryRequest,
  getAssignedSupportSessionsRequest,
  getRegisteredSupportSessionsRequest
} from '../utils/support-api-calls'
import {
  isSupportSessionAssignedToSupportUser,
  isSupportSessionExpired,
  isSupportSessionOwnedByUser
} from '../utils/support-session-utils'
import { useSupportSignalREvents } from '../utils/support-signalR-events'

export type SupportSessionDataSource =
  | 'assigned'
  | 'my-support-sessions'
  | 'history'

export type SupportSessionDataSourceParams =
  | {
      /** The data source to fetch data from. */
      dataSource: 'assigned' | 'my-support-sessions'
      /** Whether to force a fetch of the data. @default false */
      forceFetch?: boolean
      /** Whether to subscribe to support session events. @default true */
      subscribeToEvents?: boolean
    }
  | {
      /** The data source to fetch data from. */
      dataSource: 'history'
      /** Whether to force a fetch of the data. @default false */
      forceFetch?: boolean
      /** Whether to subscribe to support session events. @default true */
      subscribeToEvents?: boolean
      /** The query parameters to fetch data from. */
      query?: GetSupporSessionsHistoryQueryParams
    }

type SupportSessionDataSourceOptions<T extends SupportSessionDataSource> =
  T extends 'history'
    ? {
        /** Whether to force a fetch of the data. @default false */
        forceFetch?: boolean
        /** Whether to subscribe to support session events. @default true */
        subscribeToEvents?: boolean
        /** The query parameters to fetch data from. */
        query?: GetSupporSessionsHistoryQueryParams
      }
    : {
        /** Whether to force a fetch of the data. @default false */
        forceFetch?: boolean
        /** Whether to subscribe to support session events. @default true */
        subscribeToEvents?: boolean
      }

/**
 * A composable that returns a data source for support sessions
 */
export function useSupportSessionDataSource<T extends SupportSessionDataSource>(
  dataSource: T
) {
  const user = useUser()

  const data = shallowRef<RowDef<SupportSession>[]>([])

  function setData(sessions: RowDef<SupportSession>[]) {
    data.value = sessions
  }

  function updateSupportSession(updatedSession: RowDef<SupportSession>) {
    const newData = [...data.value]
    const index = newData.findIndex(
      (s) => s.sessionId === updatedSession.sessionId
    )
    if (index !== -1) {
      newData[index] = updatedSession
    }
    setData(newData)
  }

  function removeSupportSession(sessionId: string) {
    const newData = data.value.filter((s) => s.sessionId !== sessionId)
    setData(newData)
  }

  function addSupportSession(session: RowDef<SupportSession>) {
    const newData = [...data.value]
    newData.push({ ...session, status: session.status ?? 'new' })
    setData(newData)
    if (user.isSupportUser.value) {
      // Notification here?
    }
  }

  function clearData() {
    setData([])
  }

  const savedFetchParams = ref<SupportSessionDataSourceParams>()

  const supportSignalREvents = useSupportSignalREvents({
    onSupportSessionRegistered: (event) => {
      const supportPersonUserId = event.message.supportPersonUserId
      if (
        isSupportSessionAssignedToSupportUser(
          supportPersonUserId,
          user.id.value
        )
      ) {
        fetchData(savedFetchParams.value)
      }
    },
    onSupportSessionEnded: (event) => {
      if (user.isSupportUser.value) {
        removeSupportSession(event.message.sessionId)
        return
      }

      fetchData(savedFetchParams.value)
      if (
        !user.isSupportUser.value &&
        isSupportSessionOwnedByUser(
          event.message.requestedByUserId,
          user.id.value
        )
      ) {
        // Only notify the user if they are not a support user.
      }
    },
    onSupportSessionUpdated: (event) => {
      fetchData(savedFetchParams.value)
    }
  })

  function unsubscribeFromEvents() {
    supportSignalREvents.unsubscribeFromSupportSessionEvents()
  }

  function subscribeToEvents() {
    supportSignalREvents.subscribeToSupportSessionEvents()
    return () => {
      unsubscribeFromEvents()
    }
  }

  async function fetchData(options?: SupportSessionDataSourceOptions<T>) {
    savedFetchParams.value = { dataSource, ...options }
    if (
      dataSource === 'history' &&
      (data.value.length === 0 || options?.forceFetch)
    ) {
      const historyOptions = options as {
        query?: GetSupporSessionsHistoryQueryParams
      }
      const result = await getSupporSessionsHistoryRequest(
        historyOptions?.query
      )
      setData(result.sessions)
    }
    if (
      dataSource === 'assigned' &&
      (data.value.length === 0 || options?.forceFetch)
    ) {
      const result = await getAssignedSupportSessionsRequest()
      setData(result.sessions)
    }
    if (
      dataSource === 'my-support-sessions' &&
      (data.value.length === 0 || options?.forceFetch)
    ) {
      const result = await getRegisteredSupportSessionsRequest()
      setData(result.sessions)
    }
    if (options?.subscribeToEvents ?? true) {
      subscribeToEvents()
    }
  }

  return {
    data,
    setData,
    addSupportSession,
    updateSupportSession,
    removeSupportSession,
    clearData,
    fetchData,
    unsubscribeFromEvents,
    subscribeToEvents
  }
}

export const useSupportSessionsStore = defineStore('support-sessions', () => {
  const user = useUser()

  const historySessionsDataSource = useSupportSessionDataSource('history')

  /** All assigned support sessions for the support user. */
  const allAssignedSessionsDataSource = useSupportSessionDataSource('assigned')

  /** My support sessions. This is the list of support sessions that a common user has registered for. */
  const mySessionsDataSource = useSupportSessionDataSource(
    'my-support-sessions'
  )

  /** My active support sessions count. */
  const myActiveSessionsCount = computed(() => {
    const data = user.isSupportUser.value
      ? allAssignedSessionsDataSource.data.value
      : mySessionsDataSource.data.value
    return data.filter(
      (session: RowDef<SupportSession>) => !isSupportSessionExpired(session)
    ).length
  })

  return {
    historySessionsDataSource,
    allAssignedSessionsDataSource,
    mySessionsDataSource,
    myActiveSessionsCount
  }
})
